import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { notFound } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faChevronDown, faBolt, faStar, faTruck, faTag, faComments, faBuilding, faEnvelope, faArrowRight, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getProductPrice,
  getProductPrimaryImage,
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  HIDDEN_CATEGORY_SLUGS,
  listStorefrontCategories,
  listStorefrontProducts,
  listStorefrontTestimonials,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { absoluteUrl, brandTitle, buildFaqSchema, categoryOgImage, stripHtml } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track, trackSelectItem } from '../lib/analytics'
import { BrandTrademark, SocialLinks } from './SiteChrome'
import { WhatsAppChannelSection } from './WhatsAppChannelSection'
import { LpCrossLinks } from './LpCrossLinks'
import { AdNewsProofSection } from './HomeNewsSection'
import { getGrosirLandingPath } from '../lib/grosirLanding'
import { listStorefrontProductsServer } from '../lib/storefront'
import { listDashboardNewsPosts } from '../lib/news'

const WA_NUMBER = '6285704011028'
const LP_PRODUCT_COUNT = 8
export const LP_WALL_COUNT = 24
export const LP_HERO_IMAGE_TRANSFORM = { width: 800, height: 800, smart: true, format: 'webp', quality: 72 } as const
export const LP_PRODUCT_IMAGE_TRANSFORM = { width: 320, height: 320, smart: true, format: 'webp', quality: 58 } as const
const LP_WALL_IMAGE_TRANSFORM = { width: 380, format: 'webp', quality: 55 } as const

export function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// The testimonials API returns proof photos (chat/order screenshots) with no
// per-person text, so the "wall of proof" is built from the real images.
export function extractTestimonialImages(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  const urls: string[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const media = (entry as Record<string, unknown>).media_items
    if (!Array.isArray(media)) continue
    for (const item of media) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const isImage = record.media_type === 'image' || record.media_type == null
      const url = typeof record.url === 'string' ? record.url : typeof record.storage_url === 'string' ? record.storage_url : null
      if (isImage && url) urls.push(url)
    }
  }
  return urls
}

// Category-tailored FAQ for a hesitant first-time wholesale buyer.
function buildLpFaq(rawName: string): FaqItem[] {
  const name = rawName.trim() || 'produk ini'
  return [
    {
      q: `Berapa minimum order ${name}?`,
      a: `Minimum order berbeda untuk setiap kategori. Buka ketentuan minimum order, lalu kirim nama produk dan jumlah yang Anda rencanakan agar admin dapat memeriksa satuan pembeliannya.`,
      link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
    },
    {
      q: `Bagaimana cara meminta harga grosir ${name}?`,
      a: `Kirim nama produk, jumlah kebutuhan, dan kota tujuan. Admin akan memeriksa stok lalu mengirim harga dan ongkir dalam quotation.`,
    },
    {
      q: `Apakah ukuran dan warna dapat dicampur?`,
      a: `Ketentuannya mengikuti model dan susunan stok. Kirim pembagian ukuran atau warna yang Anda butuhkan agar admin dapat memeriksanya sebelum quotation dibuat.`,
    },
    {
      q: `Apakah saya dapat meminta sampel lebih dulu?`,
      a: `Ketersediaan sampel perlu diperiksa per produk. Total biayanya terdiri dari harga satuan produk dan ongkir ke kota tujuan.`,
    },
    {
      q: `Bagaimana proses pembayarannya?`,
      a: `Pembayaran dilakukan melalui transfer BCA atau Mandiri. Admin memeriksa bukti transfer dan mengonfirmasi pembayaran sebelum pesanan disiapkan.`,
    },
    {
      q: `Berapa lama pesanan dikirim?`,
      a: `Waktu penyiapan mengikuti isi dan jumlah pesanan. Waktu perjalanan mengikuti kota tujuan, ekspedisi, dan jenis layanan. Admin akan memberikan estimasi keduanya sebelum pesanan diproses dan mengirim nomor resi setelah paket diserahkan kepada ekspedisi.`,
    },
    {
      q: `Saya baru akan mulai menjual. Informasi apa yang perlu saya kirim?`,
      a: `Kirim kategori, target pembeli, perkiraan jumlah, dan kota tujuan. Admin akan menjelaskan pilihan produk, MOQ, quotation, serta pengiriman. Anda dapat meminta informasi sebelum memesan.`,
    },
  ]
}

const KESET_ADS_FAQ: FaqItem[] = [
  {
    q: 'Berapa minimum order keset dan karpet?',
    a: 'Minimum order mengikuti model dan susunan kemasannya. Kirim kode produk serta jumlah yang Anda rencanakan agar admin dapat memastikan satuan pembelian sebelum membuat quotation.',
  },
  {
    q: 'Apakah motif keset bisa dipilih?',
    a: 'Pilihan motif mengikuti seri dan stok saat pemesanan. Sebutkan kode produk serta motif yang dicari. Admin akan memeriksa apakah pesanan dapat dipilih per motif atau mengikuti kelompok motif.',
  },
  {
    q: 'Bagaimana ongkir pesanan keset dihitung?',
    a: 'Admin menghitung ongkir setelah model, jumlah, dan kota tujuan diketahui. Ukuran kemasan serta layanan ekspedisi juga memengaruhi biaya pengiriman.',
  },
  {
    q: 'Data apa yang perlu dikirim untuk meminta quotation?',
    a: 'Kirim kode atau nama model, jumlah per model, motif yang dibutuhkan, dan kota tujuan. Admin akan memeriksa stok, minimum order, harga, serta ongkir untuk pesanan tersebut.',
  },
]

export function getGrosirLpFaq(slug: string, name: string): FaqItem[] {
  return slug === 'keset-karpet' ? KESET_ADS_FAQ : buildLpFaq(name)
}

export async function loadGrosirLpData(slug: string, { skipSupplementalContent = false } = {}) {
  if (HIDDEN_CATEGORY_SLUGS.has(slug)) throw notFound()

  const [config, categoriesResult, productsResult, testimonialsRaw, newsPosts] = await Promise.all([
    getStorefrontConfig().catch(() => null),
    listStorefrontCategories()
      .then((categories) => ({ categories, failed: false }))
      .catch(() => ({ categories: [] as StorefrontCategory[], failed: true })),
    listStorefrontProducts({
      category: slug,
      page: 1,
      per_page: LP_PRODUCT_COUNT,
      sort_by: 'published_at',
      sort_dir: 'desc',
    })
      .then((response) => ({ response, failed: false }))
      .catch(() => ({
        failed: true,
        response: { items: [] as StorefrontProduct[], total: 0, page: 1, per_page: LP_PRODUCT_COUNT, total_pages: 0 },
      })),
    skipSupplementalContent ? Promise.resolve([]) : listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
    skipSupplementalContent ? Promise.resolve([]) : listDashboardNewsPosts().catch(() => []),
  ])

  // Unknown slugs must 404: without this, any /lp/grosir-<anything> URL
  // renders a thin auto-generated page with its own canonical (soft-404
  // doorway space). Real category LPs and static overrides are unaffected.
  const categoryFromApi = categoriesResult.categories.find((item) => item.slug === slug) ?? null
  if (!categoryFromApi && !categoriesResult.failed) throw notFound()

  const productCategory = productsResult.response.items
    .flatMap((product) => product.categories)
    .find((item) => item.slug === slug)
  const categoryFromProduct: StorefrontCategory | null = productCategory ? {
    ...productCategory,
    description: null,
    image_url: null,
    parent_id: null,
    sort_order: 0,
    children: [],
  } : null
  const fallbackName = slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
  const category = categoryFromApi ?? categoryFromProduct ?? {
    id: `temporary-${slug}`,
    name: fallbackName,
    slug,
    description: null,
    image_url: null,
    parent_id: null,
    sort_order: 0,
    children: [],
  }

  return {
    routeSlug: slug,
    config,
    category,
    products: productsResult.response.items,
    productPage: productsResult.response.page,
    productTotalPages: productsResult.response.total_pages,
    productsUnavailable: productsResult.failed || categoriesResult.failed,
    testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
    newsPosts,
  }
}

type LoadedGrosirLpData = Awaited<ReturnType<typeof loadGrosirLpData>>
export type GrosirLpData = Omit<LoadedGrosirLpData, 'productsUnavailable'> & {
  productsUnavailable?: boolean
}

export function buildGrosirLpHead({ loaderData, slug, path }: { loaderData?: GrosirLpData; slug: string; path: string }) {
  const category = loaderData?.category
  const name = category?.name?.trim() || slug
  const title = brandTitle(`Grosir ${name}`)
  const description = `Lihat pilihan ${name} untuk pembelian grosir. Kirim nama produk, jumlah, dan kota tujuan melalui WhatsApp untuk meminta quotation.`

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      // Ads-only destinations: kept out of the organic index so the matching
      // /kategori/* page is the single canonical target for these keywords.
      { name: 'robots', content: 'noindex, follow' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: absoluteUrl(path) },
      { property: 'og:image', content: categoryOgImage(category) },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: categoryOgImage(category) },
    ],
    links: [
      { rel: 'canonical', href: absoluteUrl(path) },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(buildFaqSchema(getGrosirLpFaq(slug, name))),
      },
    ],
  }
}

// Amber highlighter swipe behind the hook keyword — the page's signature.
export function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation sesuai jumlah',
    desc: 'Admin merinci produk, jumlah, harga, dan ongkir sebelum Anda melakukan pembayaran.',
  },
  {
    icon: faStar,
    title: 'Dokumentasi layanan',
    desc: 'Lihat contoh penyiapan dan pengiriman pesanan Dropify dari berbagai kategori.',
  },
  {
    icon: faTruck,
    title: 'Pengiriman antarkota',
    desc: 'Kirim kota tujuan agar admin dapat membandingkan layanan ekspedisi dan memperkirakan ongkir.',
  },
  {
    icon: faComments,
    title: 'Pemesanan lewat WhatsApp',
    desc: 'Kirim kode produk, varian, jumlah, dan kota tujuan dalam satu pesan agar admin dapat memeriksanya.',
  },
]

export function buildFitItems(slug: string, rawName: string): string[] {
  const name = rawName.trim() || 'produk ini'
  const buyerBySlug: Record<string, string[]> = {
    'kaos-kaki': ['Toko kaos kaki untuk bayi, anak, wanita, atau pria.', 'Penjual perlengkapan sekolah dan olahraga.', 'Reseller yang memerlukan ukuran dan motif berbeda.', 'Tim pengadaan yang memesan kaos kaki seragam.'],
    'manshet-sarung-tangan': ['Toko busana muslim dan aksesori hijab.', 'Reseller manset lengan dan handsock.', 'Sekolah atau pesantren yang memesan dalam jumlah besar.', 'Penjual yang membutuhkan pilihan warna dan model.'],
    'mukena-premium': ['Toko perlengkapan ibadah.', 'Toko oleh-oleh haji dan umroh.', 'Reseller mukena dewasa atau anak.', 'Pembeli hampers dan seragam yang membutuhkan jumlah grosir.'],
    'bra-lingerie': ['Toko pakaian dalam wanita.', 'Reseller yang memilih produk berdasarkan kode model.', 'Penjual yang memerlukan size chart per model.', 'Pembeli grosir bra, lingerie, atau set pakaian tidur.'],
    'cd-underwear': ['Toko pakaian dalam pria, wanita, atau anak.', 'Reseller yang memerlukan ukuran dan bahan per model.', 'Penjual marketplace yang menyusun stok per kode produk.', 'Pembeli grosir yang membutuhkan komposisi varian.'],
    'piyama-oneset': ['Toko pakaian tidur.', 'Reseller yang memilih motif dan ukuran per model.', 'Penjual live shopping yang memerlukan kode produk jelas.', 'Pembeli grosir piyama anak atau dewasa.'],
    'camisol-longtorso': ['Toko pakaian dalam dan busana muslim.', 'Reseller camisole atau longtorso.', 'Penjual yang memerlukan pilihan ukuran dan warna.', 'Pembeli grosir yang membandingkan potongan antarproduk.'],
    handuk: ['Toko perlengkapan rumah tangga.', 'Reseller handuk mandi, wajah, atau anak.', 'Penginapan dan usaha yang memerlukan jumlah besar.', 'Pembeli yang membandingkan ukuran, bahan, dan gramasi.'],
    'keset-karpet': ['Toko perlengkapan rumah.', 'Reseller keset dan karpet.', 'Pengelola kos, kantor, atau usaha.', 'Pembeli grosir yang memerlukan pilihan ukuran dan motif.'],
    'perlengkapan-bayi': ['Toko perlengkapan bayi.', 'Reseller hampers kelahiran.', 'Penjual yang memerlukan rincian isi set.', 'Pembeli grosir yang memeriksa bahan dan ukuran.'],
    'baju-anak': ['Toko pakaian anak.', 'Reseller yang menyusun stok berdasarkan ukuran.', 'Penjual yang membutuhkan rincian bahan dan model.', 'Pembeli grosir yang memerlukan komposisi warna.'],
  }
  if (buyerBySlug[slug]) return buyerBySlug[slug]
  return [
    `Reseller yang membeli ${name} untuk dijual kembali.`,
    `Toko online atau offline yang membutuhkan ${name} dalam jumlah grosir.`,
    `Pembeli yang perlu memeriksa varian dan satuan ${name} sebelum order.`,
    `Tim pengadaan yang membutuhkan quotation ${name}.`,
  ]
}

const OBJECTIONS = [
  {
    worry: 'Produk apa yang perlu dipilih lebih dahulu?',
    answer: 'Kirim jenis produk, jumlah, dan kota tujuan. Admin akan menjelaskan pilihan barang, MOQ, quotation, pembayaran, dan pengiriman.',
  },
  {
    worry: 'Bagaimana menghitung margin?',
    answer: 'Minta quotation sesuai produk dan jumlah. Hitung margin setelah harga beli, ongkir, kemasan, biaya platform, promosi, dan retur.',
  },
  {
    worry: 'Bagaimana menentukan modal awal?',
    answer: 'MOQ berbeda per kategori. Periksa jumlah minimum dan ketentuan campur varian sebelum menentukan modal awal.',
  },
  {
    worry: 'Bagaimana memeriksa stok sebelum menerima pesanan pelanggan?',
    answer: 'Kirim kode produk dan jumlah yang dibutuhkan. Admin akan mengonfirmasi ketersediaan sebelum pembayaran.',
  },
]

export function WhatsAppButton({
  message,
  label,
  category,
  product,
  placement,
  className,
}: {
  message: string
  label: string
  category: string
  product?: StorefrontProduct
  placement: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement,
          lp_category: category,
          ...(product
            ? {
                lead_item_name: product.name,
                items: [{ item_id: product.id, item_name: product.name }],
              }
            : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

export function GrosirLandingPage({
  data,
  showSocksPromo = false,
  isAdsRoute = false,
}: {
  data: GrosirLpData
  showSocksPromo?: boolean
  isAdsRoute?: boolean
}) {
  const {
    routeSlug: slug,
    category,
    products,
    productPage,
    productTotalPages,
    productsUnavailable,
    config,
    testimonialImages,
    newsPosts,
  } = data

  const name = category?.name?.trim() || slug
  const isKesetAdsRoute = isAdsRoute && slug === 'keset-karpet'
  const categoryDescription = stripHtml(category?.description)
  const heroDescription = isKesetAdsRoute
    ? 'Bandingkan keset lantai 40 x 60 cm, seri bulu, dan karpet karakter crystal velvet. Sampaikan jenis yang dicari, perkiraan jumlah, serta kota tujuan. Admin akan membantu mempersempit pilihan sebelum menyiapkan quotation.'
    : isAdsRoute
      ? `Pilih model ${name}, lalu kirim perkiraan jumlah dan kota tujuan. Admin akan memeriksa stok, harga grosir, serta ongkir sebelum Anda memesan.`
      : categoryDescription || `Lihat pilihan ${name}, kemudian kirim produk, jumlah, dan kota tujuan untuk meminta quotation.`
  const hasCategoryImage = Boolean(category?.image_url)
  const heroImage = toExternalImage(category?.image_url, LP_HERO_IMAGE_TRANSFORM) || '/logo512.png'
  const faqItems = getGrosirLpFaq(slug, name)
  const fitItems = buildFitItems(slug, name)

  const heroMessage = isKesetAdsRoute
    ? 'Halo Dropify, saya ingin membeli keset atau karpet secara grosir. Jenis atau model yang dipertimbangkan: ___. Perkiraan jumlah: ___. Motif yang dicari: ___. Kota tujuan: ___. Mohon bantu arahkan pilihannya dan konfirmasi MOQ, stok, harga, serta ongkir.'
    : isAdsRoute
      ? `Halo Dropify, saya ingin membeli ${name} secara grosir. Model: ___. Perkiraan jumlah: ___. Kota tujuan: ___. Mohon periksa stok, harga, dan ongkirnya.`
      : `Halo Dropify, saya ingin membeli ${name} secara grosir. Mohon kirim informasi produk, MOQ, stok, dan cara meminta quotation.`
  const softMessage = `Halo Dropify, saya sedang membandingkan ${name}. Mohon jelaskan pilihan produk dan ketentuan ordernya.`
  const ctaMessage = isKesetAdsRoute
    ? heroMessage
    : `Halo Dropify, saya ingin menjual ${name}. Mohon kirim katalog, MOQ, dan cara memesan.`
  const productCatalog = (
    <LpInPageProductCatalog
      categorySlug={slug}
      categoryName={name}
      initialProducts={products}
      initialPage={productPage}
      initialTotalPages={productTotalPages}
      unavailable={productsUnavailable}
      heading={isKesetAdsRoute
        ? 'Pilih keset atau karpet per kode produk'
        : isAdsRoute ? `Pilih ${name} yang ingin Anda cek` : undefined}
      description={isKesetAdsRoute
        ? 'Buka detail produk untuk memeriksa model yang menarik. Anda juga dapat langsung mengirim nama produk, perkiraan jumlah, dan kota tujuan kepada admin.'
        : isAdsRoute ? 'Klik produk untuk melihat detailnya. Saat menghubungi admin, kirim nama produk, jumlah, dan kota tujuan agar pengecekan pertama lebih lengkap.' : undefined}
      showProductDetailLink={isKesetAdsRoute}
    />
  )

  return (
    <div
      className="min-h-screen bg-white"
      data-lp-pattern={slug === 'handuk' ? 'handuk-control' : 'shared-grosir-lp'}
    >
      <LpUrgencyStrip verifiedOnly={isAdsRoute} />
      <LpMinimalHeader message={heroMessage} category={name} />

      <main>
      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              Untuk reseller, pemilik toko, dan tim pengadaan
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              {isAdsRoute ? <>Grosir {name} untuk <Mark>stok toko dan reseller</Mark></> : <>Pilih {name} grosir <Mark>berdasarkan model dan jumlah.</Mark></>}
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">{heroDescription}</p>

            <div className="mt-6 inline-flex items-baseline gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Harga dan stok</span>
              <span className="text-lg font-black leading-none text-white">Dikonfirmasi melalui quotation</span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label={isAdsRoute ? 'Minta harga dan cek stok' : 'Minta quotation grosir'}
                category={name}
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              {isAdsRoute ? (
                <a
                  href={`#lp-product-section-${slug}`}
                  onClick={() => track('lp_browse_catalog', { lp_category: name, cta_placement: 'hero_secondary' })}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  Lihat pilihan produk
                  <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
                </a>
              ) : (
                <a
                  href={waLink(softMessage)}
                  {...whatsAppCtaProps('landing_page', { cta_placement: 'hero_secondary', lp_category: name })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  Tanyakan produk dan MOQ →
                </a>
              )}
            </div>
            <p className="mt-3 text-sm text-slate-400">
              {isAdsRoute ? 'Siapkan nama produk atau model, perkiraan jumlah, dan kota tujuan.' : 'Kirim pertanyaan lebih dahulu. Admin akan menjelaskan MOQ dan ketentuan order.'}
            </p>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
              <img
                src={heroImage}
                alt={hasCategoryImage ? `Grosir ${name} Dropify` : 'Dropify distributor grosir Surabaya'}
                width="800"
                height="800"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className={hasCategoryImage ? 'w-full aspect-square object-cover' : 'w-full aspect-square bg-slate-900 object-contain p-20 md:p-28'}
              />
            </div>
            <div className="absolute -bottom-4 -left-3 sm:left-4 flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400">
                <FontAwesomeIcon icon={faBolt} className="h-4 w-4 text-slate-900" />
              </span>
              <div className="leading-tight">
                <p className="text-xs font-black uppercase tracking-wide text-slate-900">Harga & stok</p>
                <p className="text-[11px] text-slate-500">Konfirmasi via WhatsApp</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isAdsRoute && productCatalog}

      {isKesetAdsRoute ? (
        <>
          <KesetSelectionGuide message={heroMessage} category={name} />
          <LpWhyUsSection title="Data yang diperiksa sebelum quotation keset dibuat" />
          <AdNewsProofSection posts={newsPosts} />
          <TestimonialWall images={testimonialImages} />
          <LpBrandProtectionNotice />
        </>
      ) : (
        <>
          <AdNewsProofSection posts={newsPosts} />

          {!isAdsRoute && <WhatsAppChannelSection showSocksPromo={showSocksPromo} />}

          <LpBrandProtectionNotice />

          {/* Who it's for — let the visitor self-identify */}
          <LpFitSection items={fitItems} />

          {/* Why us */}
          <LpWhyUsSection title={`Informasi pemesanan grosir ${name}`} />

          {!isAdsRoute && productCatalog}

          {/* Scalev-style wall of proof */}
          <TestimonialWall images={testimonialImages} />

          {isAdsRoute && <WhatsAppChannelSection showSocksPromo={showSocksPromo} />}

          {/* Objection handling — catch the unsure visitor */}
          <LpObjectionsSection softMessage={softMessage} category={name} />
        </>
      )}

      {/* FAQ */}
      <LpFaqSection categoryName={name} faqItems={faqItems} />

      {/* Final CTA */}
      <LpFinalCtaSection title={`Minta quotation grosir ${name}`} message={ctaMessage} category={name} />

      {/* Cross-links to sibling landing pages */}
      {!isKesetAdsRoute && <LpCrossLinks currentPath={getGrosirLandingPath(slug)} />}
      </main>

      {/* Footer */}
      <LpFooter config={config} category={name} />
    </div>
  )
}

function KesetSelectionGuide({ message, category }: { message: string; category: string }) {
  const groups = [
    {
      title: 'Mulai dari ukuran 40 x 60 cm',
      detail: 'AX-200, BU-200, HD-200, dan CX-200. Bandingkan motif, permukaan, dan kemasan per kode sebelum menentukan jumlah.',
    },
    {
      title: 'Mencari keset bulu',
      detail: 'DD-11A dan DD-11B tersedia sebagai seri berbeda. Minta rincian warna, ukuran, dan susunan kemasan untuk model yang dipilih.',
    },
    {
      title: 'Mencari motif karakter',
      detail: 'Lini MR-9 sampai MR-12 serta beberapa kelompok motif karakter. Periksa ukuran dan pilihan motif yang tersedia per seri.',
    },
  ]

  return (
    <section className="bg-slate-50 py-12 md:py-16" aria-labelledby="keset-selection-heading">
      <div className="mx-auto max-w-6xl px-4">
        <h2 id="keset-selection-heading" className="text-2xl font-black text-slate-900 md:text-3xl">
          Mulai dari ukuran, permukaan, atau motif yang dicari
        </h2>
        <p className="mt-3 max-w-3xl text-slate-600">
          Kode produk dapat dipilih bersama admin. Sampaikan jenis penggunaan, perkiraan jumlah, dan kota tujuan agar pilihan awal dapat dipersempit sebelum stok serta harga diperiksa.
        </p>
        <div className="mt-7 grid gap-4 md:grid-cols-3">
          {groups.map((group) => (
            <div key={group.title} className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-lg font-bold text-slate-900">{group.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{group.detail}</p>
            </div>
          ))}
        </div>
        <div className="mt-7 flex flex-col gap-4 rounded-xl bg-white p-5 ring-1 ring-slate-200 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-2xl text-sm text-slate-700">
            Sudah menemukan model yang sesuai? Kirim nama produk dan jumlahnya. Jika masih membandingkan, sebutkan ukuran atau motif yang dicari.
          </p>
          <WhatsAppButton
            message={message}
            label="Minta bantuan pilih keset"
            category={category}
            placement="selection_guide"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-green-700 px-5 py-3 text-sm font-bold text-white hover:bg-green-800"
          />
        </div>
      </div>
    </section>
  )
}

export function LpInPageProductCatalog({
  categorySlug,
  categoryName,
  initialProducts,
  initialPage,
  initialTotalPages,
  unavailable = false,
  heading = 'Bandingkan produk dalam kategori ini',
  description = 'Berikut sebagian produk dalam kategori ini. Buka detail produk, lalu kirim kode dan jumlah kepada admin untuk memastikan stok.',
  showProductDetailLink = false,
}: {
  categorySlug: string
  categoryName: string
  initialProducts: StorefrontProduct[]
  initialPage: number
  initialTotalPages: number
  unavailable?: boolean
  heading?: string
  description?: string
  showProductDetailLink?: boolean
}) {
  const [products, setProducts] = useState(initialProducts)
  const [nextPage, setNextPage] = useState(initialPage + 1)
  const [hasMoreProducts, setHasMoreProducts] = useState(initialPage < initialTotalPages)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  if (products.length === 0 && !hasMoreProducts && !unavailable) return null

  async function loadMoreProducts() {
    if (isLoading || !hasMoreProducts) return

    setIsLoading(true)
    setLoadError(false)

    try {
      const response = await listStorefrontProductsServer({
        data: {
          category: categorySlug,
          page: nextPage,
          per_page: LP_PRODUCT_COUNT,
          sort_by: 'published_at',
          sort_dir: 'desc',
        },
      })

      setProducts((currentProducts) => {
        const productById = new Map(currentProducts.map((product) => [product.id, product]))
        for (const product of response.items) productById.set(product.id, product)
        return [...productById.values()]
      })
      setNextPage(response.page + 1)
      setHasMoreProducts(response.page < response.total_pages)
      track('lp_load_more_products', {
        lp_category: categoryName,
        page_number: response.page,
        items_loaded: response.items.length,
      })
    } catch {
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const gridId = `lp-product-grid-${categorySlug}`
  const sectionId = `lp-product-section-${categorySlug}`

  return (
    <section id={sectionId} className="scroll-mt-6 bg-slate-50 py-14" aria-labelledby={`${gridId}-title`}>
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-8 max-w-2xl">
          <h2 id={`${gridId}-title`} className="text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
            {heading}
          </h2>
          <p className="mt-2 text-base text-slate-500">{description}</p>
        </div>
        {products.length > 0 ? (
          <div id={gridId} className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-live="polite">
            {products.map((product) => (
              <LpProductCard
                key={product.id}
                product={product}
                category={categoryName}
                showProductDetailLink={showProductDetailLink}
              />
            ))}
          </div>
        ) : (
          <div id={gridId} className="rounded-2xl border border-blue-100 bg-white p-6 shadow-sm md:flex md:items-center md:justify-between md:gap-8 md:p-8" role="status">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Katalog sedang disinkronkan</p>
              <h3 className="mt-2 text-xl font-black text-slate-900">Admin tetap dapat memeriksa produk {categoryName}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Kirim jenis produk, perkiraan jumlah, varian yang dicari, dan kota tujuan. Admin akan memeriksa pilihan yang tersedia serta menyiapkan quotation.
              </p>
            </div>
            <a
              href={waLink(`Halo Dropify, saya ingin meminta katalog ${categoryName}. Jenis produk: ___. Perkiraan jumlah: ___. Varian: ___. Kota tujuan: ___.`)}
              target="_blank"
              rel="noopener noreferrer"
              {...whatsAppCtaProps('landing_page', { cta_placement: 'catalog_unavailable', lp_category: categoryName })}
              className="mt-5 inline-flex min-h-12 flex-shrink-0 items-center justify-center gap-2 rounded-xl bg-green-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800 md:mt-0"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
              Minta katalog melalui WhatsApp
            </a>
          </div>
        )}
        {hasMoreProducts && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={loadMoreProducts}
              disabled={isLoading}
              aria-controls={gridId}
              aria-busy={isLoading}
              className="inline-flex min-h-12 items-center gap-2 rounded-lg border border-slate-300 px-6 py-3 text-base font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900 disabled:cursor-wait disabled:opacity-60"
            >
              {isLoading ? 'Memuat produk...' : 'Tampilkan produk lainnya'}
              <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5" />
            </button>
            {loadError && (
              <p role="alert" className="mt-3 text-sm font-medium text-red-700">
                Produk belum berhasil dimuat. Silakan coba lagi.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

// --- Shared LP building blocks -------------------------------------------
// Exported so bespoke single-category LPs (e.g. /grosir-kaos-kaki) can reuse
// the exact same conversion chrome without copy-pasting. Rendering here must
// stay identical for the generic /lp/grosir-$slug pages.

export function LpUrgencyStrip({ verifiedOnly = false }: { verifiedOnly?: boolean }) {
  return (
    <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
      <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
      {verifiedOnly
        ? 'Pesan grosir melalui WhatsApp. Admin akan mengonfirmasi harga dan stok sebelum Anda memesan.'
        : 'Pesan grosir melalui WhatsApp. Admin akan mengonfirmasi harga dan stok sebelum Anda memesan.'}
    </div>
  )
}

// Minimal header — logo + single CTA, no nav links to avoid click leaks
export function LpMinimalHeader({ message, category }: { message: string; category: string }) {
  return (
    <header className="border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
          <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
          <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
        </a>
        <WhatsAppButton
          message={message}
          label="Minta quotation"
          category={category}
          placement="header"
          className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
        />
      </div>
    </header>
  )
}

// Brand-protection notice — Dropify only takes orders via WhatsApp, not marketplaces
export function LpBrandProtectionNotice() {
  return (
    <section className="bg-white pt-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
          <p className="text-base leading-relaxed text-slate-700">
            <span className="font-bold text-slate-900">Pemesanan resmi Dropify dilakukan melalui WhatsApp.</span> Dropify tidak membuka toko di Tokopedia, Shopee, Lazada, atau marketplace lain. Pastikan nomor tujuan sesuai dengan kontak yang tercantum di website ini.
          </p>
        </div>
      </div>
    </section>
  )
}

export function LpFitSection({ items }: { items: string[] }) {
  return (
    <section className="py-14 bg-white border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-9 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Siapa yang dilayani halaman ini?</h2>
          <p className="mt-2 text-base text-slate-500">Daftar berikut membantu Anda menilai apakah kategori dan cara pemesanan grosirnya sesuai dengan kebutuhan.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
          {items.map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
              </span>
              <p className="text-base leading-relaxed text-slate-700">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function LpWhyUsSection({ title }: { title: string }) {
  return (
    <section className="py-14 bg-white border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-9 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">{title}</h2>
          <p className="mt-2 text-base text-slate-500">Setiap poin menjelaskan data atau proses yang dapat diperiksa sebelum pembayaran.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {WHY_US_ITEMS.map((item) => (
            <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
              </div>
              <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function LpObjectionsSection({ softMessage, category }: { softMessage: string; category: string }) {
  return (
    <section className="py-14 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-9 max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Hal yang perlu dipastikan sebelum order</h2>
          <p className="mt-2 text-base text-slate-500">Periksa pilihan produk, MOQ, perhitungan biaya, dan stok sebelum menerima pesanan dari pelanggan Anda.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {OBJECTIONS.map((item) => (
            <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
              <p className="text-base font-bold text-slate-900">{item.worry}</p>
              <div className="mt-2 flex gap-2.5">
                <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
          <p className="text-base font-semibold text-slate-700">Kirim pertanyaan Anda kepada admin sebelum menentukan pesanan.</p>
          <WhatsAppButton
            message={softMessage}
            label="Tanyakan kepada admin"
            category={category}
            placement="objection_section"
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
          />
        </div>
      </div>
    </section>
  )
}

export function LpFinalCtaSection({ title, message, category }: { title: string; message: string; category: string }) {
  return (
    <section className="bg-[#0B1120] py-16">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">{title}</h2>
        <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
          Kirim nama produk, jumlah, dan kota tujuan. Admin akan memeriksa stok serta menyiapkan quotation sebelum Anda memesan.
        </p>
        <div className="mt-7 flex justify-center">
          <WhatsAppButton
            message={message}
            label="Minta quotation melalui WhatsApp"
            category={category}
            placement="closing"
            className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
          />
        </div>
        <p className="mt-4 text-sm text-amber-300/90">
          <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
          Minta admin memeriksa stok dan quotation sebelum Anda menerima pesanan dari pembeli.
        </p>

        {/* Legitimacy: accepted payment methods, reassures cold buyers */}
        <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
              <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
            </div>
            <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
              <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// Footer with legitimacy + contact (no nav links — keeps the ad funnel tight)
export function LpFooter({ config, category }: { config: StorefrontConfig | null; category: string }) {
  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  return (
    <footer className="border-t border-slate-100 bg-white py-10">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
          <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
          PT Dropify Distribusi Indonesia
        </p>
          <p className="mt-1.5 text-xs text-slate-500">Pemesanan grosir melalui WhatsApp</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
          <a
            href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
            {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: category })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
            {contactPhone}
          </a>
          <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
            <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
            {contactEmail}
          </a>
        </div>
        <div className="mt-5 flex justify-center">
          <SocialLinks variant="light" />
        </div>
        <p className="mt-5 text-xs text-slate-500">© Dropify. Semua hak cipta dilindungi.</p>
        <BrandTrademark className="mt-1 text-xs text-slate-500" />
      </div>
    </footer>
  )
}

export function LpProductCard({
  product,
  category,
  showProductDetailLink = false,
}: {
  product: StorefrontProduct
  category: string
  showProductDetailLink?: boolean
}) {
  const image = getProductPrimaryImage(product, LP_PRODUCT_IMAGE_TRANSFORM)
  const message = `Halo Dropify, saya tertarik ${product.name} untuk pembelian grosir ${category}. Mohon cek MOQ, informasi stok, dan quotation untuk jumlah ___.`
  const productHref = `/produk/${product.slug || product.id}`
  const selectProduct = () =>
    trackSelectItem(
      {
        item_id: product.id,
        item_name: product.name,
        item_category: category,
        price: getProductPrice(product),
      },
      'grosir_kaos_kaki_products',
    )
  const askOnWhatsApp = whatsAppCtaProps('landing_page', {
      cta_placement: 'product_card',
      lp_category: category,
      lead_item_name: product.name,
      items: [{ item_id: product.id, item_name: product.name }],
    })

  if (showProductDetailLink) {
    return (
      <article className="group flex flex-col overflow-hidden rounded-xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg">
        <a
          href={productHref}
          onClick={selectProduct}
          aria-label={`Lihat detail ${product.name}`}
          className="relative block overflow-hidden bg-slate-100"
          style={{ height: 200 }}
        >
          <img src={image} alt={product.name} width="320" height="320" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </a>
        <div className="flex flex-1 flex-col p-4">
          <a href={productHref} onClick={selectProduct} className="hover:text-blue-600">
            <h3 className="text-sm font-semibold leading-snug text-slate-800 transition-colors group-hover:text-blue-600 line-clamp-2">{product.name}</h3>
          </a>
          <a
            href={productHref}
            onClick={selectProduct}
            className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-blue-200 px-3 py-2.5 text-sm font-bold text-blue-700 transition-colors hover:border-blue-600 hover:bg-blue-50"
          >
            Lihat detail produk
            <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
          </a>
          <a
            href={waLink(message)}
            {...askOnWhatsApp}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-700 px-3 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
            Periksa stok dan harga
          </a>
        </div>
      </article>
    )
  }

  return (
    <a
      href={waLink(message)}
      {...askOnWhatsApp}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col overflow-hidden rounded-xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg"
    >
      <div className="relative overflow-hidden bg-slate-100" style={{ height: 200 }}>
        <img src={image} alt={product.name} width="320" height="320" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-sm font-semibold leading-snug text-slate-800 line-clamp-2">{product.name}</h3>
        <span className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-700 px-3 py-2.5 text-sm font-bold text-white transition-colors group-hover:bg-green-800">
          <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
          Periksa stok dan harga
        </span>
      </div>
    </a>
  )
}

export function TestimonialWall({ images }: { images: string[] }) {
  if (images.length === 0) return null

  const columns: string[][] = [[], [], []]
  images.forEach((src, i) => columns[i % 3].push(src))
  const durations = ['38s', '48s', '42s']
  const directions = ['lp-up', 'lp-down', 'lp-up']

  return (
    <section className="relative overflow-hidden bg-slate-50 py-14">
      <style>{`
        @keyframes lp-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes lp-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .lp-track { animation: lp-up var(--dur,40s) linear infinite; }
        .lp-track.lp-down { animation-name: lp-down; }
        .lp-wall:hover .lp-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .lp-track { animation: none; } }
      `}</style>

      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Dokumentasi layanan Dropify
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dokumentasi pesanan grosir yang telah kami tangani</h2>
          <p className="mt-2 text-base text-slate-500">Dokumentasi ini berasal dari pemesanan, penyiapan barang, dan pengiriman beberapa kategori Dropify.</p>
        </div>
      </div>

      <div className="lp-wall relative mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {columns.map((column, ci) => (
            <div key={ci} className={`h-[460px] sm:h-[560px] overflow-hidden ${ci === 2 ? 'hidden lg:block' : ''}`}>
              <div className={`flex flex-col gap-4 lp-track ${directions[ci] === 'lp-down' ? 'lp-down' : ''}`} style={{ '--dur': durations[ci] } as CSSProperties}>
                {[...column, ...column].map((src, i) => (
                  <WallCard key={`${ci}-${i}`} src={toExternalImage(src, LP_WALL_IMAGE_TRANSFORM)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
    </section>
  )
}

function WallCard({ src }: { src: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <img src={src} alt="Dokumentasi pemesanan dan pengiriman Dropify" width="380" height="507" loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover" />
    </figure>
  )
}

export function LpFaqSection({ categoryName, faqItems }: { categoryName: string; faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan tentang grosir {categoryName}</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'lp' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm leading-relaxed text-slate-600">
          Baru pertama kali memesan? Anda dapat meminta alur pemeriksaan kiriman sebelum transfer.{' '}
          <a href="/jaminan-ekspedisi" className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900">Baca panduan verifikasi lewat ekspedisi pilihan</a>.
        </p>
      </div>
    </section>
  )
}
